import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { api } from "@shared/routes";
import { useAuth } from "@/hooks/use-auth";

function showBrowserNotification(title: string, body: string) {
  if (!("Notification" in window)) return;
  if (Notification.permission === "granted") {
    new Notification(title, { body, icon: "/favicon.ico" });
  } else if (Notification.permission === "default") {
    Notification.requestPermission().then((permission) => {
      if (permission === "granted") {
        new Notification(title, { body, icon: "/favicon.ico" });
      }
    });
  }
}

/**
 * Subscribes to Supabase Realtime postgres changes for the core tables
 * (laudos, lotes, faturas, lancamentos, audit_logs) and invalidates the
 * corresponding React Query caches so the UI stays in sync with external
 * changes (e.g. from the mobile app).
 *
 * Must be mounted inside AuthProvider and QueryClientProvider.
 */
export function useRealtimeSync() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  useEffect(() => {
    // Only subscribe while authenticated
    if (!user) return;

    const channel = supabase
      .channel("laudolegal-realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "laudos" },
        () => {
          queryClient.invalidateQueries({ queryKey: [api.laudos.list.path] });
          queryClient.invalidateQueries({ queryKey: [api.laudos.stats.path] });
          queryClient.invalidateQueries({ queryKey: [api.laudos.get.path] });
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "lotes" },
        () => {
          queryClient.invalidateQueries({ queryKey: [api.lotes.list.path] });
          queryClient.invalidateQueries({ queryKey: [api.lotes.get.path] });
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "faturas" },
        () => {
          queryClient.invalidateQueries({
            queryKey: [api.financeiro.faturas.list.path],
          });
          queryClient.invalidateQueries({
            queryKey: [api.financeiro.stats.path],
          });
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "lancamentos" },
        () => {
          queryClient.invalidateQueries({
            queryKey: [api.financeiro.lancamentos.list.path],
          });
          queryClient.invalidateQueries({
            queryKey: [api.financeiro.stats.path],
          });
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "audit_logs" },
        () => {
          queryClient.invalidateQueries({
            queryKey: [api.auditoria.list.path],
          });
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "empresas" },
        () => {
          queryClient.invalidateQueries({ queryKey: [api.empresas.list.path] });
          queryClient.invalidateQueries({ queryKey: [api.empresas.get.path] });
          queryClient.invalidateQueries({ queryKey: [api.empresas.parceiros.path] });
          queryClient.invalidateQueries({ queryKey: [api.veiculos.parceiros.path] });
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "user_profiles" },
        () => {
          queryClient.invalidateQueries({ queryKey: [api.usuarios.list.path] });
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "pecas" },
        () => {
          queryClient.invalidateQueries({ queryKey: [api.pecas.list.path] });
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "veiculos" },
        () => {
          queryClient.invalidateQueries({ queryKey: [api.veiculos.list.path] });
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "invite_codes" },
        () => {
          queryClient.invalidateQueries({ queryKey: [api.inviteCodes.list.path] });
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "user_invites" },
        () => {
          queryClient.invalidateQueries({ queryKey: [api.userInvites.list.path] });
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "perguntas" },
        () => {
          queryClient.invalidateQueries({ queryKey: [api.pecaPerguntas.list.path] });
          queryClient.invalidateQueries({ queryKey: [api.perguntasPadrao.list.path] });
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "banco_perguntas_globais" },
        () => {
          queryClient.invalidateQueries({ queryKey: [api.bancoPerguntas.list.path] });
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "laudo_pecas" },
        () => {
          queryClient.invalidateQueries({ queryKey: [api.laudos.get.path] });
        },
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notificacoes" },
        (payload) => {
          queryClient.invalidateQueries({ queryKey: [api.notificacoes.list.path] });
          const row = payload.new as Record<string, unknown>;
          if (row.usuario_id === user?.id) {
            showBrowserNotification(
              String(row.titulo ?? "Nova notificação"),
              String(row.mensagem ?? ""),
            );
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient, user]);
}
